import { computed, ref, nextTick, readonly } from 'vue';
import { defineStore } from 'pinia';
import { astaV2Search, loadUrvalsgrupper, loadLarosaten } from '../Asta/astaService.ts';
import { ResultatSorteringsGrupper, ResultatSortering } from '../../types/AstaTypes';
import type {
  Urvalsgrupp,
  AstaSettings,
  AstaSortSettings,
  AstaTexter,
  AstaDisplaySettings,
  AstaFilterSettings,
  AstaResultatStatusType,
  Larosate,
  AstaSökparmetrar,
  AstaResultItemType,
  TillfalleType,
  AstaQuery,
} from '../../types/AstaTypes';

const setEquals = (xs: Set<any>, ys: Set<any>): boolean => xs.size === ys.size && [...xs].every((x) => ys.has(x));

export default defineStore('asta', () => {
  const defaultUrvalsgrupper = ['BI', 'HP'];
  const settings = ref<AstaSettings>({} as AstaSettings);
  const page = ref(1);
  const antalObjekt = ref(0);
  const result = ref<Array<AstaResultItemType>>([]);
  const tillfalle = ref<TillfalleType>('urv2');
  const allaUrvalsgrupper = ref<Urvalsgrupp[]>([]);
  const allaLarosaten = ref<Larosate[]>([]);
  const resultsLoading = ref(false);
  const display = ref<AstaDisplaySettings>({
    vygroup: 'antagna',
    vy: 'antagningspoang-urv2',
    urvalsgrupper: [],
  });
  const filter = ref<AstaFilterSettings>({
    utbildningstyp: null,
    larosaten: [],
  });
  const sökParametrar = ref<AstaSökparmetrar>({
    searchterm: '',
    searchfor: '',
    searchkategori: '',
  });
  const sort = ref<AstaSortSettings>({
    sorteringskolumn: undefined,
    sorteringsurvalsgrupp: undefined,
    sorteringsordningdesc: false,
  });

  const setResultLimitCookie = (cvalue: number) => {
    document.cookie = 'resultlimit=' + cvalue;
  };

  const getResultLimitFromCookie = (): number => {
    let name = 'resultlimit=';
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return parseInt(c.substring(name.length, c.length));
      }
    }
    return -1;
  };

  const init = (settingsData: AstaSettings, data: AstaResultatStatusType) => {
    settings.value = settingsData;
    var limit = getResultLimitFromCookie();
    if (limit && !isNaN(limit)) {
      var validLimits = [10, 25, 50, 100];
      if (validLimits.includes(limit)) {
        settings.value.pageSize = limit;
      }
    }

    setValuesFromAstaObject(data);
  };

  const setValuesFromAstaObject = (data: AstaResultatStatusType) => {
    display.value = {
      vygroup: data.vygroup,
      vy: data.vy,
      urvalsgrupper: data.urvalsgrupper,
    };
    filter.value = {
      utbildningstyp: data.utbildningstyp,
      larosaten: data.larosaten,
    };
    sökParametrar.value = {
      searchterm: data.searchterm,
      searchfor: data.searchfor,
      searchkategori: data.searchkategori,
    };
    sort.value = {
      sorteringskolumn: data.sorteringskolumn,
      sorteringsurvalsgrupp: data.sorteringsurvalsgrupp,
      sorteringsordningdesc: data.sorteringsordningdesc,
    };
    tillfalle.value = data.tillfalle;
    const isSTSearch = data.searchterm.startsWith('ST');
    if (isSTSearch) {
      display.value.vy = 'antagningspoang-urv1';
      //anropar även showVySelect('antagna'), men den verkar inte tillföra mycket som inte är komponenthanterat
    }
  };

  const getQueryObject = (): AstaQuery => ({
    // searchfor: string;
    // searchterm: string;
    // searchkategori: string;
    ...sökParametrar.value,
    // utbildningstyp?: utbildningstypType;
    // larosaten: string[];
    ...filter.value,
    // vy: VyType;
    vy: display.value.vy,
    ...sort.value,
    tillfalle: tillfalle.value,
    page: page.value,
  });

  const search = async (updateUrval: boolean, updateLarosaten: boolean) => {
    resultsLoading.value = true;
    if (updateUrval === false && updateLarosaten === false) {
      scrollToResultTop();
    }
    const response = await astaV2Search(getQueryObject(), settings.value.pageSize).catch((error: any) => {
      console.error('Error while fetching data', error);
    });
    resultsLoading.value = false;
    //internalState.data = response!;
    result.value = response?.data ?? [];
    antalObjekt.value = response?.antalObjekt ?? 0;
    await rebindTooltip();

    if (updateUrval) {
      refreshUrvalsgrupper();
    }
    if (updateLarosaten) {
      refreshLarosaten();
    }
  };
  async function rebindTooltip() {
    await nextTick();
    ($('[data-toggle="tooltip"]') as any).tooltip();
  }
  function scrollToResultTop() {
    var element = document.getElementById('ScrollTarget');
    element?.scrollIntoView();
  }
  const setDisplay = (newData: AstaDisplaySettings) => {
    const data = { ...newData };
    const vyChanged = display.value.vy != data.vy;
    const vyGroupChanged = display.value.vygroup != data.vygroup;
    var i = data.vy.lastIndexOf('-');
    if (vyChanged) {
      //kanske måste sättas initialt om i if?
      tillfalle.value = data.vy.substring(i + 1) as TillfalleType;
    }
    if (vyGroupChanged) {
      if (data.vygroup == 'sokande') {
        data.vy = 'sokande-sok';
        tillfalle.value = 'sok';
      } else {
        data.vy = 'antagningspoang-urv2';
        data.urvalsgrupper = [...defaultUrvalsgrupper];
        tillfalle.value = 'urv2';
      }
    }
    display.value = {
      vygroup: data.vygroup,
      vy: data.vy,
      urvalsgrupper: [...data.urvalsgrupper],
    };
    if (vyChanged) {
      refreshUrvalsgrupper();
    }
    rebindTooltip();
  };

  const setFilter = (data: AstaFilterSettings) => {
    const isUbildningsTypChanged = filter.value.utbildningstyp != data.utbildningstyp;
    const isLarosatenChanged = !setEquals(new Set(filter.value.larosaten), new Set(data.larosaten));
    if (isUbildningsTypChanged || isLarosatenChanged) {
      filter.value = {
        utbildningstyp: data.utbildningstyp,
        larosaten: isUbildningsTypChanged ? [] : [...data.larosaten],
      };
      display.value.urvalsgrupper = [...defaultUrvalsgrupper];
      page.value = 1;
      search(isUbildningsTypChanged || isLarosatenChanged, isUbildningsTypChanged);
    }
  };

  const setSort = (data: AstaSortSettings) => {
    sort.value = { ...data };
    page.value = 1;
    search(false, false);
  };
  const setPageSize = (size: number) => {
    if (settings.value.pageSize != size) {
      settings.value.pageSize = size;
      setResultLimitCookie(size);
      page.value = 1;
      search(false, false);
    }
  };

  const extractUrvalsgrupperFromResult = (data: AstaResultItemType[]) => {
    return data
      ?.filter((x) => x.urval1)
      .flatMap((x) => x.urval1!.urvalsgrupper)
      .map((x) => x.urvalsgruppId);
  };

  //efter sök, input[name="AntagnaSokande"].change, select[name="Vy"].change
  const refreshUrvalsgrupper = async () => {
    let tmp = extractUrvalsgrupperFromResult(result.value) ?? [];
    let loaded: Urvalsgrupp[] = [];
    let r = await loadUrvalsgrupper(getQueryObject());

    if (r.data?.length) {
      loaded = r.data;
    } else {
      let d = { ...getQueryObject() };
      d.tillfalle = d.tillfalle == 'urv1' ? 'urv2' : 'urv1';
      r = await loadUrvalsgrupper(d);
      if (r.data?.length) {
        loaded = r.data;
      } else {
        console.log('AS:refreshUrvalsgrupper NO RESULT!');
      }
    }

    const existing = allaUrvalsgrupper.value.map((x) => x.urvalsgruppId);
    const loadedNames = loaded.map((x) => x.urvalsgruppId);
    allaUrvalsgrupper.value = [...new Set([...tmp, ...existing, ...loadedNames])].map((x) => ({ urvalsgruppId: x }));
  };

  const refreshLarosaten = async () => {
    let response = await loadLarosaten(getQueryObject());
    if (response.data?.length) {
      allaLarosaten.value = response.data;
      return;
    }
    //no result..
    let p = { ...getQueryObject() };
    p.tillfalle = p.tillfalle == 'urv1' ? 'urv2' : 'urv1';
    response = await loadLarosaten(p);
    if (response.data?.length) {
      allaLarosaten.value = response.data;
      return;
    }
  };

  const refreshLists = () => {
    refreshLarosaten();
    refreshUrvalsgrupper();
  };

  const visningsalternativAntagna = computed(() => [
    { value: 'antagningspoang-urv1', label: texter.value.searchresultblock.antagningspoang_urv1.text },
    { value: 'antagningspoang-urv2', label: texter.value.searchresultblock.antagningspoang_urv2.text },
    { value: 'reserver-och-antagna-urv1', label: texter.value.searchresultblock.reserver_och_antagna_urv1.text },
    { value: 'reserver-och-antagna-urv2', label: texter.value.searchresultblock.reserver_och_antagna_urv2.text },
    { value: 'konsfordelning-urv1', label: texter.value.searchresultblock.konsfordelning_urv1.text },
    { value: 'konsfordelning-urv2', label: texter.value.searchresultblock.konsfordelning_urv2.text },
    { value: 'aldersfordelning-urv1', label: texter.value.searchresultblock.aldersfordelning_urv1.text },
    { value: 'aldersfordelning-urv2', label: texter.value.searchresultblock.aldersfordelning_urv2.text },
  ]);

  const visningsalternativSokande = computed(() => [
    { value: 'sokande-sok', label: texter.value.searchresultblock.reserver_och_antagna_sok.text },
    { value: 'konsfordelning-sok', label: texter.value.searchresultblock.konsfordelning_sok.text },
    { value: 'aldersfordelning-sok', label: texter.value.searchresultblock.aldersfordelning_sok.text },
  ]);

  const texter = computed((): AstaTexter => settings.value.Texter);

  const HasUrval1ResultData = computed(() => result.value.filter((x) => (x.urval1?.urvalsgrupper?.length ?? 0) > 0).length > 0);
  const HasUrval2ResultData = computed(() => result.value.filter((x) => (x.urval2?.urvalsgrupper?.length ?? 0) > 0).length > 0);

  const infoText = computed(() => {
    const dict = {
      'antagningspoang-urv1': Asta.Texter.AdmissionPointsUrv1Desc,
      'antagningspoang-urv2': Asta.Texter.AdmissionPointsUrv2Desc,
      'reserver-och-antagna-urv1': Asta.Texter.ReservAndAdmittedUrv1Desc,
      'reserver-och-antagna-urv2': Asta.Texter.ReservAndAdmittedUrv2Desc,
      'konsfordelning-urv1': Asta.Texter.GenderDistributionUrv1Desc,
      'konsfordelning-urv2': Asta.Texter.GenderDistributionUrv2Desc,
      'aldersfordelning-urv1': Asta.Texter.AgeDistributionUrv1Desc,
      'aldersfordelning-urv2': Asta.Texter.AgeDistributionUrv2Desc,
      'sokande-sok': Asta.Texter.NumberOfApplicantsSokDesc,
      'konsfordelning-sok': Asta.Texter.GenderDistributionSokDesc,
      'aldersfordelning-sok': Asta.Texter.AgeDistributionSokDesc,
    };
    return dict[display.value.vy] ?? '';
  });

  //exempel på paging när den byggs..
  const nextPage = () => {
    page.value++;
    search(false, false);
  };
  const setPageNumber = (pageNumber: number) => {
    page.value = pageNumber;
    search(false, false);
  };
  const resultatSorteringsGrupper = computed(() => {
    let resultatSorteringsGrupper: ResultatSorteringsGrupper = {} as ResultatSorteringsGrupper;
    if (display.value.vy === 'antagningspoang-urv1' || display.value.vy === 'antagningspoang-urv2') {
      let urvalsgrupper = [...display.value.urvalsgrupper];
      if (urvalsgrupper.length === 0) {
        urvalsgrupper = ['BI', 'HP']; // Om inga urvalsgrupper är valda, visa default
      }
      const valuesToSortInOrder = ['BI', 'BII', 'BF', 'HP', 'AU', 'SA', 'ÖS'];
      urvalsgrupper.sort((a, b) => {
        const aIndex = valuesToSortInOrder.indexOf(a.toUpperCase());
        const bIndex = valuesToSortInOrder.indexOf(b.toUpperCase());
        if (aIndex === -1 && bIndex === -1) {
          return 0;
        }
        if (aIndex === -1) {
          return 1;
        }
        if (bIndex === -1) {
          return -1;
        }
        return aIndex - bIndex;
      });

      let sorteringPoäng = [] as ResultatSortering[];
      let sorteringAntagna = [] as ResultatSortering[];
      let sorteringReserver = [] as ResultatSortering[];
      urvalsgrupper.forEach((urvalsgrupp) => {
        if (urvalsgrupp === 'BI') {
          sorteringPoäng.push({
            namn: 'Gymnasiebetyg utan komplettering (BI) - högsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringPoäng.push({
            namn: 'Gymnasiebetyg utan komplettering (BI) - lägsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Gymnasiebetyg med komplettering (BI) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Gymnasiebetyg med komplettering (BI) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Gymnasiebetyg med komplettering (BI) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Gymnasiebetyg med komplettering (BI) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
        } else if (urvalsgrupp === 'BII') {
          sorteringPoäng.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - högsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringPoäng.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - lägsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Gymnasiebetyg med komplettering (BII) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
        } else if (urvalsgrupp === 'BF') {
          sorteringPoäng.push({
            namn: 'Folkhögskola (BF) - högst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringPoäng.push({
            namn: 'Folkhögskola (BF) - lägst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: false, sorteringsurvalsgrupp: 'BI' },
          });
          sorteringAntagna.push({
            namn: 'Folkhögskola (BF) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Folkhögskola (BF) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false, sorteringsurvalsgrupp: 'BI' },
          });
          sorteringReserver.push({
            namn: 'Folkhögskola (BF) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Folkhögskola (BF) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false, sorteringsurvalsgrupp: 'BI' },
          });
        } else if (urvalsgrupp === 'HP') {
          sorteringPoäng.push({
            namn: 'Högskoleprov (HP) - högsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringPoäng.push({
            namn: 'Högskoleprov (HP) - lägsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Högskoleprov (HP) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: 'Högskoleprov (HP) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Högskoleprov (HP) - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: 'Högskoleprov (HP) - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
        } else {
          sorteringPoäng.push({
            namn: urvalsgrupp + ' - högsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringPoäng.push({
            namn: urvalsgrupp + ' - lägsta',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'lagstaantagnapoang', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: urvalsgrupp + ' - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringAntagna.push({
            namn: urvalsgrupp + ' - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: urvalsgrupp + ' - flest',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true, sorteringsurvalsgrupp: urvalsgrupp },
          });
          sorteringReserver.push({
            namn: urvalsgrupp + ' - minst',
            vy: display.value.vy,
            sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false, sorteringsurvalsgrupp: urvalsgrupp },
          });
        }
      });
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        {
          namn: 'Urvalsgrupp',
          label: 'Sortera på urvalsgrupp',
          soretering: [],
        },
        {
          namn: 'Poäng',
          label: 'Sortera på poäng',
          soretering: sorteringPoäng,
        },
        {
          namn: 'Antagna',
          label: 'Sortera på antagna',
          soretering: sorteringAntagna,
        },
        {
          namn: 'Reserver',
          label: 'Sortera på reserver',
          soretering: sorteringReserver,
        },
      ];
    } else if (display.value.vy === 'reserver-och-antagna-urv1' || display.value.vy === 'reserver-och-antagna-urv2') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Titel', soretering: [] },
        {
          namn: 'Antal',
          label: 'Sortera på antal',
          soretering: [
            { namn: 'Antagna - högsta', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: true } },
            { namn: 'Antagna - lägsta', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna', sorteringsordningdesc: false } },
            { namn: 'Reserver - högsta', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: true } },
            { namn: 'Reserver - lägsta', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver', sorteringsordningdesc: false } },
          ],
        },
      ];
    } else if (display.value.vy === 'konsfordelning-urv1' || display.value.vy === 'konsfordelning-urv2') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Kön', soretering: [] },
        {
          namn: 'Antagna',
          label: 'Sortera på antagna',
          soretering: [
            { namn: 'Män - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'manligaantagna', sorteringsordningdesc: true } },
            { namn: 'Män - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'manligaantagna', sorteringsordningdesc: false } },
            { namn: 'Kvinnor - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligaantagna', sorteringsordningdesc: true } },
            { namn: 'Kvinnor - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligaantagna', sorteringsordningdesc: false } },
          ],
        },
        {
          namn: 'Reserver',
          label: 'Sortera på reserver',
          soretering: [
            { namn: 'Män - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'manligareserver', sorteringsordningdesc: true } },
            { namn: 'Män - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'manligareserver', sorteringsordningdesc: false } },
            { namn: 'Kvinnor - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligareserver', sorteringsordningdesc: true } },
            { namn: 'Kvinnor - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligareserver', sorteringsordningdesc: false } },
          ],
        },
      ];
    } else if (display.value.vy === 'aldersfordelning-urv1' || display.value.vy === 'aldersfordelning-urv2') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Åldersintervall', soretering: [] },
        {
          namn: 'Antagna',
          label: 'Sortera på antagna',
          soretering: [
            { namn: 'Under 25 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna24arelleryngre', sorteringsordningdesc: true } },
            { namn: 'Under 25 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna24arelleryngre', sorteringsordningdesc: false } },
            { namn: '25-34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna25till34ar', sorteringsordningdesc: true } },
            { namn: '25-34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna25till34ar', sorteringsordningdesc: false } },
            { namn: 'Över 34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna35arelleraldre', sorteringsordningdesc: true } },
            { namn: 'Över 34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'antagna35arelleraldre', sorteringsordningdesc: false } },
          ],
        },
        {
          namn: 'Reserver',
          label: 'Sortera på reserver',
          soretering: [
            { namn: 'Under 25 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver24arelleryngre', sorteringsordningdesc: true } },
            { namn: 'Under 25 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver24arelleryngre', sorteringsordningdesc: false } },
            { namn: '25-34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver25till34ar', sorteringsordningdesc: true } },
            { namn: '25-34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver25till34ar', sorteringsordningdesc: false } },
            { namn: 'Över 34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver35arelleraldre', sorteringsordningdesc: true } },
            { namn: 'Över 34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'reserver35arelleraldre', sorteringsordningdesc: false } },
          ],
        },
      ];
    } else if (display.value.vy === 'sokande-sok') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Titel', soretering: [] },
        {
          namn: 'Antal',
          label: 'Sortera på antagna',
          soretering: [
            { namn: 'Förstahand - högsta', vy: display.value.vy, sortering: { sorteringskolumn: 'forstahandsokande', sorteringsordningdesc: true } },
            { namn: 'Förstahand - lägsta', vy: display.value.vy, sortering: { sorteringskolumn: 'forstahandsokande', sorteringsordningdesc: false } },
            { namn: 'Sökande totalt - högsta', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande', sorteringsordningdesc: true } },
            { namn: 'Sökande totalt - lägsta', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande', sorteringsordningdesc: false } },
          ],
        },
      ];
    } else if (display.value.vy === 'konsfordelning-sok') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Kön', soretering: [] },
        {
          namn: 'Antal',
          label: 'Sortera på antal',
          soretering: [
            { namn: 'Män - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'manligasokande', sorteringsordningdesc: true } },
            { namn: 'Män - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'manligasokande', sorteringsordningdesc: false } },
            { namn: 'Kvinnor - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligasokande', sorteringsordningdesc: true } },
            { namn: 'Kvinnor - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'kvinnligasokande', sorteringsordningdesc: false } },
          ],
        },
      ];
    } else if (display.value.vy === 'aldersfordelning-sok') {
      resultatSorteringsGrupper.grupp = [
        {
          namn: 'Namn',
          label: 'Sortera på namn',
          soretering: [
            { namn: 'Namn, A-Ö', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: false } },
            { namn: 'Namn, Ö-A', vy: display.value.vy, sortering: { sorteringskolumn: 'anmalningsalternativ', sorteringsordningdesc: true } },
          ],
        },
        { namn: 'Ålderintervall', soretering: [] },
        {
          namn: 'Antal',
          label: 'Sortera på antal',
          soretering: [
            { namn: 'Under 25 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande24arelleryngre', sorteringsordningdesc: true } },
            { namn: 'Under 25 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande24arelleryngre', sorteringsordningdesc: false } },
            { namn: '25-34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande25till34ar', sorteringsordningdesc: true } },
            { namn: '25-34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande25till34ar', sorteringsordningdesc: false } },
            { namn: 'Över 34 - flest', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande35arelleraldre', sorteringsordningdesc: true } },
            { namn: 'Över 34 - minst', vy: display.value.vy, sortering: { sorteringskolumn: 'sokande35arelleraldre', sorteringsordningdesc: false } },
          ],
        },
      ];
    }
    return resultatSorteringsGrupper;
  });

  return {
    init,
    settings,
    sökParametrar,
    texter,
    search,
    setDisplay,
    setFilter,
    getQueryObject,
    setSort,
    setPageSize,
    setPageNumber,
    display: readonly(display), //computed((): AstaDisplaySettings => ({ ...display.value, urvalsgrupper: [...display.value.urvalsgrupper] })),
    filter: readonly(filter), // computed((): AstaFilterSettings => ({ ...filter.value, larosaten: [...filter.value.larosaten] })),
    sort: readonly(sort), //computed((): AstaSortSettings => ({ ...sort.value })),
    result,
    visningsalternativAntagna,
    visningsalternativSokande,
    allaUrvalsgrupper,
    setValuesFromAstaObject,
    allaLarosaten,
    refreshLists,
    HasUrval1ResultData,
    HasUrval2ResultData,
    setResultLimitCookie,
    nextPage,
    page,
    resultsLoading,
    antalObjekt,
    infoText,
    resultatSorteringsGrupper,
    defaultUrvalsgrupper: readonly(defaultUrvalsgrupper),
  };
});
